import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import uiIt from '../i18n/ui.it.json';
import uiEn from '../i18n/ui.en.json';
import uiDe from '../i18n/ui.de.json';
import uiFr from '../i18n/ui.fr.json';
import servicesIt from '../i18n/services.it.json';
import servicesEn from '../i18n/services.en.json';
import servicesDe from '../i18n/services.de.json';
import servicesFr from '../i18n/services.fr.json';
import { localize } from '../lib/routes';

const translations = {
  it: {
    nav: {
      home: 'Home',
      services: 'Servizi',
      about: 'Chi Siamo',
      contact: 'Contatti',
      faq: 'FAQ',
      news: 'News'
    },
    hero: {
      quote: '"Tanto più notaio, tanto meno giudice."',
      author: 'Francesco Carnelutti',
      subtitle: 'Studio Notarile Associato',
      description: 'Un punto di riferimento nel panorama notarile veronese. Sicurezza giuridica, competenza e rapidità al servizio dei nostri clienti.',
      cta: 'Richiedi Consulenza',
      ctaSecondary: 'I Nostri Servizi'
    },
    services: {
      title: 'I Nostri Servizi',
      subtitle: 'Competenza e professionalità al vostro servizio',
      items: {
        immobiliare: {
          title: 'Immobiliare',
          desc: 'Compravendite, mutui, divisioni, permute e ogni altro atto relativo a beni immobili con la massima sicurezza giuridica.'
        },
        successioni: {
          title: 'Successioni e Testamenti',
          desc: 'Assistenza completa in materia successoria: testamenti, dichiarazioni di successione, accettazione eredità.'
        },
        aziende: {
          title: 'Aziende e Società',
          desc: 'Costituzione società, modifiche statutarie, trasformazioni, fusioni, cessioni quote e operazioni straordinarie.'
        },
        patrimoniali: {
          title: 'Sistemazioni Patrimoniali',
          desc: 'Donazioni, patti di famiglia, fondi patrimoniali, trust e pianificazione successoria.'
        },
        procure: {
          title: 'Procure ed Autentiche',
          desc: 'Procure generali e speciali, autentiche di firma, legalizzazioni per uso estero.'
        },
        consulenza: {
          title: 'Consulenza Giuridica e Fiscale',
          desc: 'Assistenza personalizzata per ottimizzare gli aspetti giuridici e fiscali delle vostre operazioni.'
        },
        agricola: {
          title: 'Impresa Agricola',
          desc: 'Coltivatori diretti, piccola proprietà contadina, imprenditore agricolo professionale.'
        },
        ipocatastali: {
          title: 'Ispezioni Ipocatastali',
          desc: 'Visure ipotecarie e catastali, sistemazioni e regolarizzazioni immobiliari.'
        },
        vidimazione: {
          title: 'Vidimazione Libri',
          desc: 'Vidimazione e bollatura libri sociali e contabili obbligatori.'
        }
      }
    },
    about: {
      title: 'Chi Siamo',
      subtitle: 'Tradizione e Innovazione',
      description: 'Lo Studio Notarile Associato Accordini & Carillo rappresenta un punto di riferimento nel panorama notarile veronese. Grazie alla consolidata esperienza e alla costante attenzione all\'evoluzione normativa, lo Studio è in grado di operare in ogni settore garantendo la massima sicurezza dei traffici giuridici - elemento essenziale della funzione notarile - unitamente alla rapidità di esecuzione richiesta dal mercato moderno.',
      team: 'Lo Studio si avvale di un qualificato team di collaboratori specializzati che opera con i più moderni strumenti tecnologici della professione, garantendo efficienza e precisione in ogni pratica.',
      notai: 'I Nostri Notai',
      notaio1: {
        name: 'Notaio Umberto Accordini',
        role: 'Notaio Associato',
        desc: 'Esperto in diritto societario e commerciale'
      },
      notaio2: {
        name: 'Notaio Carillo',
        role: 'Notaio Associato',
        desc: 'Specializzato in diritto immobiliare e successioni'
      }
    },
    contact: {
      title: 'Contattaci',
      subtitle: 'Siamo a vostra disposizione nelle nostre tre sedi',
      form: {
        name: 'Nome e Cognome',
        email: 'Email',
        phone: 'Telefono',
        subject: 'Oggetto',
        message: 'Messaggio',
        send: 'Invia Messaggio',
        success: 'Messaggio inviato con successo!',
        error: 'Errore nell\'invio. Riprova.'
      },
      offices: {
        valeggio: {
          name: 'Sede di Valeggio sul Mincio',
          address: 'Via Don Giovanni Beltrame 22',
          city: '37067 Valeggio Sul Mincio (VR)',
          phone: '+39 045 6370799'
        },
        peschiera: {
          name: 'Sede di Peschiera del Garda',
          address: 'Via Venezia 74',
          city: '37019 Peschiera del Garda (VR)',
          phone: '+39 045 640 1687'
        },
        verona: {
          name: 'Sede di Verona',
          address: 'Corso Porta Nuova 11',
          city: '37122 Verona',
          phone: '+39 045 6370799'
        }
      }
    },
    faq: {
      title: 'Domande Frequenti',
      subtitle: 'Risposte alle domande più comuni',
      items: [
        {
          q: 'Quali documenti servono per un atto di compravendita?',
          a: 'Per un atto di compravendita sono necessari: documento di identità valido, codice fiscale, titolo di provenienza dell\'immobile, documentazione urbanistica e catastale, APE (Attestato di Prestazione Energetica).'
        },
        {
          q: 'Quanto costa una consulenza notarile?',
          a: 'La prima consulenza informativa è gratuita. Per preventivi dettagliati relativi ad atti specifici, vi invitiamo a contattarci direttamente.'
        },
        {
          q: 'Come posso prenotare un appuntamento?',
          a: 'Potete contattarci telefonicamente o tramite il modulo di contatto presente su questo sito. Vi risponderemo nel più breve tempo possibile.'
        },
        {
          q: 'È possibile stipulare atti fuori dall\'orario di ufficio?',
          a: 'Sì, previo accordo è possibile organizzare stipule in orari particolari o durante il fine settimana per esigenze specifiche.'
        },
        {
          q: 'Quali sono i tempi per la stipula di un atto?',
          a: 'I tempi variano in base alla tipologia di atto e alla completezza della documentazione. In media, una compravendita richiede 15-30 giorni dalla raccolta dei documenti.'
        }
      ]
    },
    news: {
      title: 'News e Aggiornamenti',
      subtitle: 'Le ultime novità normative e dello studio',
      readMore: 'Leggi tutto'
    },
    footer: {
      description: 'Studio Notarile Associato con sedi a Verona, Valeggio sul Mincio e Peschiera del Garda. Professionalità e competenza al servizio dei nostri clienti.',
      quickLinks: 'Link Rapidi',
      contacts: 'Contatti',
      vatNumber: 'P.IVA',
      madeWith: 'Made with love by'
    }
  },
  en: {
    nav: {
      home: 'Home',
      services: 'Services',
      about: 'About Us',
      contact: 'Contact',
      faq: 'FAQ',
      news: 'News'
    },
    hero: {
      quote: '"The more the notary, the less the judge."',
      author: 'Francesco Carnelutti',
      subtitle: 'Associated Notary Firm',
      description: 'A point of reference in the Veronese notarial landscape. Legal security, expertise and efficiency at the service of our clients.',
      cta: 'Request Consultation',
      ctaSecondary: 'Our Services'
    },
    services: {
      title: 'Our Services',
      subtitle: 'Competence and professionalism at your service',
      items: {
        immobiliare: {
          title: 'Real Estate',
          desc: 'Property sales, mortgages, divisions, exchanges and all real estate transactions with maximum legal security.'
        },
        successioni: {
          title: 'Inheritance & Wills',
          desc: 'Complete assistance in inheritance matters: wills, succession declarations, estate acceptance.'
        },
        aziende: {
          title: 'Companies & Corporations',
          desc: 'Company formation, statutory changes, transformations, mergers, share transfers and extraordinary operations.'
        },
        patrimoniali: {
          title: 'Asset Planning',
          desc: 'Donations, family agreements, trust funds, trusts and succession planning.'
        },
        procure: {
          title: 'Powers of Attorney',
          desc: 'General and special powers of attorney, signature authentications, legalizations for foreign use.'
        },
        consulenza: {
          title: 'Legal & Tax Advisory',
          desc: 'Personalized assistance to optimize the legal and tax aspects of your transactions.'
        },
        agricola: {
          title: 'Agricultural Business',
          desc: 'Direct farmers, small rural property, professional agricultural entrepreneur.'
        },
        ipocatastali: {
          title: 'Land Registry Services',
          desc: 'Mortgage and cadastral inspections, property regularizations and settlements.'
        },
        vidimazione: {
          title: 'Book Certification',
          desc: 'Certification and stamping of mandatory corporate and accounting books.'
        }
      }
    },
    about: {
      title: 'About Us',
      subtitle: 'Tradition and Innovation',
      description: 'Studio Notarile Associato Accordini & Carillo is a reference point in the Veronese notarial landscape. Thanks to consolidated experience and constant attention to regulatory evolution, the firm is able to operate in every sector, ensuring maximum security of legal transactions - an essential element of notarial function - together with the speed of execution required by the modern market.',
      team: 'The firm relies on a qualified team of specialized collaborators who work with the most modern technological tools of the profession, ensuring efficiency and precision in every practice.',
      notai: 'Our Notaries',
      notaio1: {
        name: 'Notary Umberto Accordini',
        role: 'Associate Notary',
        desc: 'Expert in corporate and commercial law'
      },
      notaio2: {
        name: 'Notary Carillo',
        role: 'Associate Notary',
        desc: 'Specialized in real estate and inheritance law'
      }
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'We are at your disposal in our three offices',
      form: {
        name: 'Full Name',
        email: 'Email',
        phone: 'Phone',
        subject: 'Subject',
        message: 'Message',
        send: 'Send Message',
        success: 'Message sent successfully!',
        error: 'Sending error. Please try again.'
      },
      offices: {
        valeggio: {
          name: 'Valeggio sul Mincio Office',
          address: 'Via Don Giovanni Beltrame 22',
          city: '37067 Valeggio Sul Mincio (VR)',
          phone: '+39 045 6370799'
        },
        peschiera: {
          name: 'Peschiera del Garda Office',
          address: 'Via Venezia 74',
          city: '37019 Peschiera del Garda (VR)',
          phone: '+39 045 640 1687'
        },
        verona: {
          name: 'Verona Office',
          address: 'Corso Porta Nuova 11',
          city: '37122 Verona',
          phone: '+39 045 6370799'
        }
      }
    },
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Answers to the most common questions',
      items: [
        {
          q: 'What documents are needed for a property sale?',
          a: 'For a property sale you need: valid ID, tax code, property title documents, urban planning and cadastral documentation, EPC (Energy Performance Certificate).'
        },
        {
          q: 'How much does a notarial consultation cost?',
          a: 'The first informative consultation is free. For detailed estimates related to specific deeds, please contact us directly.'
        },
        {
          q: 'How can I book an appointment?',
          a: 'You can contact us by phone or through the contact form on this website. We will respond as soon as possible.'
        },
        {
          q: 'Is it possible to sign deeds outside office hours?',
          a: 'Yes, by prior agreement it is possible to organize signings at special times or during the weekend for specific needs.'
        },
        {
          q: 'What are the timeframes for signing a deed?',
          a: 'Times vary depending on the type of deed and the completeness of the documentation. On average, a sale requires 15-30 days from document collection.'
        }
      ]
    },
    news: {
      title: 'News & Updates',
      subtitle: 'Latest regulatory news and firm updates',
      readMore: 'Read more'
    },
    footer: {
      description: 'Associated Notary Firm with offices in Verona, Valeggio sul Mincio and Peschiera del Garda. Professionalism and competence at your service.',
      quickLinks: 'Quick Links',
      contacts: 'Contacts',
      vatNumber: 'VAT Number',
      madeWith: 'Made with love by'
    }
  },
  de: {
    nav: {
      home: 'Startseite',
      services: 'Leistungen',
      about: 'Über Uns',
      contact: 'Kontakt',
      faq: 'FAQ',
      news: 'Neuigkeiten'
    },
    hero: {
      quote: '"Je mehr Notar, desto weniger Richter."',
      author: 'Francesco Carnelutti',
      subtitle: 'Assoziiertes Notariat',
      description: 'Ein Bezugspunkt in der Veroneser Notariatslandschaft. Rechtssicherheit, Kompetenz und Schnelligkeit im Dienst unserer Mandanten.',
      cta: 'Beratung Anfordern',
      ctaSecondary: 'Unsere Leistungen'
    },
    services: {
      title: 'Unsere Leistungen',
      subtitle: 'Kompetenz und Professionalität zu Ihrem Dienst',
      items: {
        immobiliare: {
          title: 'Immobilien',
          desc: 'Immobilienverkäufe, Hypotheken, Teilungen, Tausch und alle Immobilientransaktionen mit maximaler Rechtssicherheit.'
        },
        successioni: {
          title: 'Erbschaft & Testament',
          desc: 'Vollständige Unterstützung in Erbschaftsangelegenheiten: Testamente, Erbschaftserklärungen, Erbschaftsannahme.'
        },
        aziende: {
          title: 'Unternehmen & Gesellschaften',
          desc: 'Unternehmensgründung, Satzungsänderungen, Umwandlungen, Fusionen, Anteilsübertragungen und außerordentliche Geschäfte.'
        },
        patrimoniali: {
          title: 'Vermögensplanung',
          desc: 'Schenkungen, Familienvereinbarungen, Treuhandfonds, Trusts und Nachlassplanung.'
        },
        procure: {
          title: 'Vollmachten',
          desc: 'General- und Spezialvollmachten, Unterschriftsbeglaubigungen, Legalisierungen für den Auslandsgebrauch.'
        },
        consulenza: {
          title: 'Rechts- & Steuerberatung',
          desc: 'Persönliche Beratung zur Optimierung der rechtlichen und steuerlichen Aspekte Ihrer Transaktionen.'
        },
        agricola: {
          title: 'Landwirtschaftsbetrieb',
          desc: 'Direktlandwirte, kleine ländliche Eigentumsrechte, professioneller landwirtschaftlicher Unternehmer.'
        },
        ipocatastali: {
          title: 'Grundbuchdienste',
          desc: 'Hypotheken- und Katasterprüfungen, Immobilienregularisierungen und -abrechnungen.'
        },
        vidimazione: {
          title: 'Buchzertifizierung',
          desc: 'Zertifizierung und Stempelung von obligatorischen Unternehmens- und Buchhaltungsbüchern.'
        }
      }
    },
    about: {
      title: 'Über Uns',
      subtitle: 'Tradition und Innovation',
      description: 'Studio Notarile Associato Accordini & Carillo ist ein Bezugspunkt in der Veroneser Notarlandschaft. Dank konsolidierter Erfahrung und ständiger Aufmerksamkeit für regulatorische Entwicklungen kann die Kanzlei in jedem Sektor tätig werden und maximale Sicherheit des Rechtsverkehrs gewährleisten - ein wesentliches Element der Notarfunktion - zusammen mit der vom modernen Markt geforderten Ausführungsgeschwindigkeit.',
      team: 'Die Kanzlei verfügt über ein qualifiziertes Team spezialisierter Mitarbeiter, die mit den modernsten technologischen Werkzeugen des Berufsstandes arbeiten und Effizienz und Präzision in jeder Praxis gewährleisten.',
      notai: 'Unsere Notare',
      notaio1: {
        name: 'Notar Umberto Accordini',
        role: 'Assoziierter Notar',
        desc: 'Experte für Gesellschafts- und Handelsrecht'
      },
      notaio2: {
        name: 'Notar Carillo',
        role: 'Assoziierter Notar',
        desc: 'Spezialisiert auf Immobilien- und Erbrecht'
      }
    },
    contact: {
      title: 'Kontaktieren Sie Uns',
      subtitle: 'Wir stehen Ihnen in unseren drei Büros zur Verfügung',
      form: {
        name: 'Vollständiger Name',
        email: 'E-Mail',
        phone: 'Telefon',
        subject: 'Betreff',
        message: 'Nachricht',
        send: 'Nachricht Senden',
        success: 'Nachricht erfolgreich gesendet!',
        error: 'Sendefehler. Bitte versuchen Sie es erneut.'
      },
      offices: {
        valeggio: {
          name: 'Büro Valeggio sul Mincio',
          address: 'Via Don Giovanni Beltrame 22',
          city: '37067 Valeggio Sul Mincio (VR)',
          phone: '+39 045 6370799'
        },
        peschiera: {
          name: 'Büro Peschiera del Garda',
          address: 'Via Venezia 74',
          city: '37019 Peschiera del Garda (VR)',
          phone: '+39 045 640 1687'
        },
        verona: {
          name: 'Büro Verona',
          address: 'Corso Porta Nuova 11',
          city: '37122 Verona',
          phone: '+39 045 6370799'
        }
      }
    },
    faq: {
      title: 'Häufig Gestellte Fragen',
      subtitle: 'Antworten auf die häufigsten Fragen',
      items: [
        {
          q: 'Welche Dokumente werden für einen Immobilienverkauf benötigt?',
          a: 'Für einen Immobilienverkauf benötigen Sie: gültigen Ausweis, Steuernummer, Eigentumsurkunden, städtebauliche und katastrale Dokumentation, Energieausweis.'
        },
        {
          q: 'Was kostet eine notarielle Beratung?',
          a: 'Die erste informative Beratung ist kostenlos. Für detaillierte Kostenvoranschläge zu bestimmten Urkunden kontaktieren Sie uns bitte direkt.'
        },
        {
          q: 'Wie kann ich einen Termin buchen?',
          a: 'Sie können uns telefonisch oder über das Kontaktformular auf dieser Website erreichen. Wir werden so schnell wie möglich antworten.'
        },
        {
          q: 'Ist es möglich, Urkunden außerhalb der Bürozeiten zu unterzeichnen?',
          a: 'Ja, nach vorheriger Vereinbarung ist es möglich, Unterzeichnungen zu besonderen Zeiten oder am Wochenende zu organisieren.'
        },
        {
          q: 'Wie lange dauert die Unterzeichnung einer Urkunde?',
          a: 'Die Zeiten variieren je nach Art der Urkunde und Vollständigkeit der Dokumentation. Im Durchschnitt dauert ein Verkauf 15-30 Tage ab Dokumentensammlung.'
        }
      ]
    },
    news: {
      title: 'Neuigkeiten & Updates',
      subtitle: 'Aktuelle regulatorische Neuigkeiten und Kanzlei-Updates',
      readMore: 'Mehr lesen'
    },
    footer: {
      description: 'Assoziiertes Notariat mit Büros in Verona, Valeggio sul Mincio und Peschiera del Garda. Professionalität und Kompetenz zu Ihrem Dienst.',
      quickLinks: 'Schnelllinks',
      contacts: 'Kontakte',
      vatNumber: 'USt-IdNr.',
      madeWith: 'Erstellt mit Liebe von'
    }
  },
  fr: {
    nav: {
      home: 'Accueil',
      services: 'Services',
      about: 'À Propos',
      contact: 'Contact',
      faq: 'FAQ',
      news: 'Actualités'
    },
    hero: {
      quote: '"Plus de notaire, moins de juge."',
      author: 'Francesco Carnelutti',
      subtitle: 'Étude Notariale Associée',
      description: 'Un point de référence dans le paysage notarial véronais. Sécurité juridique, compétence et rapidité au service de nos clients.',
      cta: 'Demander une Consultation',
      ctaSecondary: 'Nos Services'
    },
    services: {
      title: 'Nos Services',
      subtitle: 'Compétence et professionnalisme à votre service',
      items: {
        immobiliare: {
          title: 'Immobilier',
          desc: 'Ventes immobilières, hypothèques, divisions, échanges et toutes transactions immobilières avec une sécurité juridique maximale.'
        },
        successioni: {
          title: 'Successions et Testaments',
          desc: 'Assistance complète en matière successorale: testaments, déclarations de succession, acceptation de succession.'
        },
        aziende: {
          title: 'Entreprises et Sociétés',
          desc: 'Constitution de sociétés, modifications statutaires, transformations, fusions, cessions de parts et opérations extraordinaires.'
        },
        patrimoniali: {
          title: 'Planification Patrimoniale',
          desc: 'Donations, pactes de famille, fonds patrimoniaux, trusts et planification successorale.'
        },
        procure: {
          title: 'Procurations',
          desc: 'Procurations générales et spéciales, authentifications de signatures, légalisations pour usage étranger.'
        },
        consulenza: {
          title: 'Conseil Juridique et Fiscal',
          desc: 'Assistance personnalisée pour optimiser les aspects juridiques et fiscaux de vos opérations.'
        },
        agricola: {
          title: 'Entreprise Agricole',
          desc: 'Agriculteurs directs, petite propriété rurale, entrepreneur agricole professionnel.'
        },
        ipocatastali: {
          title: 'Services Cadastraux',
          desc: 'Inspections hypothécaires et cadastrales, régularisations et règlements immobiliers.'
        },
        vidimazione: {
          title: 'Certification des Livres',
          desc: 'Certification et timbrage des livres sociaux et comptables obligatoires.'
        }
      }
    },
    about: {
      title: 'À Propos',
      subtitle: 'Tradition et Innovation',
      description: 'Studio Notarile Associato Accordini & Carillo représente un point de référence dans le paysage notarial véronais. Grâce à une expérience consolidée et une attention constante à l\'évolution réglementaire, le cabinet est en mesure d\'opérer dans tous les secteurs, garantissant la sécurité maximale des transactions juridiques - élément essentiel de la fonction notariale - ainsi que la rapidité d\'exécution exigée par le marché moderne.',
      team: 'Le cabinet s\'appuie sur une équipe qualifiée de collaborateurs spécialisés qui travaillent avec les outils technologiques les plus modernes de la profession, garantissant efficacité et précision dans chaque dossier.',
      notai: 'Nos Notaires',
      notaio1: {
        name: 'Notaire Umberto Accordini',
        role: 'Notaire Associé',
        desc: 'Expert en droit des sociétés et commercial'
      },
      notaio2: {
        name: 'Notaire Carillo',
        role: 'Notaire Associé',
        desc: 'Spécialisé en droit immobilier et successions'
      }
    },
    contact: {
      title: 'Contactez-Nous',
      subtitle: 'Nous sommes à votre disposition dans nos trois bureaux',
      form: {
        name: 'Nom Complet',
        email: 'Email',
        phone: 'Téléphone',
        subject: 'Sujet',
        message: 'Message',
        send: 'Envoyer le Message',
        success: 'Message envoyé avec succès!',
        error: 'Erreur d\'envoi. Veuillez réessayer.'
      },
      offices: {
        valeggio: {
          name: 'Bureau de Valeggio sul Mincio',
          address: 'Via Don Giovanni Beltrame 22',
          city: '37067 Valeggio Sul Mincio (VR)',
          phone: '+39 045 6370799'
        },
        peschiera: {
          name: 'Bureau de Peschiera del Garda',
          address: 'Via Venezia 74',
          city: '37019 Peschiera del Garda (VR)',
          phone: '+39 045 640 1687'
        },
        verona: {
          name: 'Bureau de Vérone',
          address: 'Corso Porta Nuova 11',
          city: '37122 Verona',
          phone: '+39 045 6370799'
        }
      }
    },
    faq: {
      title: 'Questions Fréquentes',
      subtitle: 'Réponses aux questions les plus courantes',
      items: [
        {
          q: 'Quels documents sont nécessaires pour une vente immobilière?',
          a: 'Pour une vente immobilière, vous avez besoin de: pièce d\'identité valide, code fiscal, titre de propriété, documentation urbanistique et cadastrale, DPE (Diagnostic de Performance Énergétique).'
        },
        {
          q: 'Combien coûte une consultation notariale?',
          a: 'La première consultation informative est gratuite. Pour des devis détaillés relatifs à des actes spécifiques, veuillez nous contacter directement.'
        },
        {
          q: 'Comment puis-je prendre rendez-vous?',
          a: 'Vous pouvez nous contacter par téléphone ou via le formulaire de contact de ce site. Nous vous répondrons dans les plus brefs délais.'
        },
        {
          q: 'Est-il possible de signer des actes en dehors des heures de bureau?',
          a: 'Oui, sur accord préalable, il est possible d\'organiser des signatures à des horaires particuliers ou le week-end.'
        },
        {
          q: 'Quels sont les délais pour la signature d\'un acte?',
          a: 'Les délais varient selon le type d\'acte et la complétude de la documentation. En moyenne, une vente nécessite 15-30 jours à partir de la collecte des documents.'
        }
      ]
    },
    news: {
      title: 'Actualités et Mises à Jour',
      subtitle: 'Dernières nouvelles réglementaires et du cabinet',
      readMore: 'Lire la suite'
    },
    footer: {
      description: 'Étude Notariale Associée avec bureaux à Vérone, Valeggio sul Mincio et Peschiera del Garda. Professionnalisme et compétence à votre service.',
      quickLinks: 'Liens Rapides',
      contacts: 'Contacts',
      vatNumber: 'N° TVA',
      madeWith: 'Fait avec amour par'
    }
  }
};

const SUPPORTED = ['it', 'en', 'de', 'fr'];
const UI = { it: uiIt, en: uiEn, de: uiDe, fr: uiFr };
const SERVICES = { it: servicesIt, en: servicesEn, de: servicesDe, fr: servicesFr };

const initialLanguage = () => {
  const fromUrl = window.location.pathname.split('/')[1];
  if (SUPPORTED.includes(fromUrl)) {
    localStorage.setItem('lang', fromUrl);
    return fromUrl;
  }
  const saved = localStorage.getItem('lang');
  if (SUPPORTED.includes(saved)) return saved;
  const browser = (navigator.language || 'it').slice(0, 2).toLowerCase();
  return SUPPORTED.includes(browser) ? browser : 'it';
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(initialLanguage);

  const setLanguage = useCallback((lang) => {
    localStorage.setItem('lang', lang);
    setLanguageState(lang);
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);
  
  const t = useCallback((key) => {
    const keys = key.split('.');
    let value = keys[0] === 'ui' ? { ui: UI[language] } : translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  }, [language]);

  const services = SERVICES[language];
  
  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, services }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const useLp = () => {
  const { language } = useLanguage();
  return useCallback((itPath) => localize(itPath, language), [language]);
};
